import { formatUnits } from '@ethersproject/units';
import { useApr, useApy } from '@ui/hooks/useApr';
import type { CoinMode } from '@ui/hooks/useCoinMode';
import { useCoinModeBalance } from '@ui/hooks/useCoinMode';
import useCoinPerStakedCoin from '@ui/hooks/useCoinPerStakedCoin';
import { useCoinUsdValue } from '@ui/hooks/useCoinUsdValue';
import aprToGrossApy from '@ui/utils/aprToGrossApy';
import apyToApyList from '@ui/utils/apyToApyList';
import { currentWallet } from '@veno-app/wallet';
import { useMemo } from 'react';

import useStakeTransactionFee from '../views/Stake/hooks/useStakeTransactionFee';

const { useIsConnected, useAccount } = currentWallet;

const useStakeViewData = (coinMode?: CoinMode) => {
  const isConnected = useIsConnected();
  const account = useAccount();
  const { data: balance, isLoading: balanceLoading } =
    useCoinModeBalance(coinMode);

  const stakeFee = useStakeTransactionFee(coinMode);
  const { data: balanceUsd, isLoading: balanceUsdLoading } = useCoinUsdValue(
    balance,
    coinMode,
  );

  // max amount is wallet balance - transaction fee, and shown value will
  // be a bit less than that because of precision issues, rather show a value
  // that's just a bit too small so that the transaction can be sent
  const maxStakeAmount =
    balance && stakeFee !== undefined
      ? stakeFee.eq(0)
        ? balance
        : Math.max(
            0,
            Math.floor(
              100000000 *
                (balance -
                  Math.ceil(parseFloat(formatUnits(stakeFee, 18)) * 100000000) /
                    100000000),
            ) / 100000000,
          )
      : 0;

  const { data: coinPerStakedCoin, isLoading: coinPerStakedCoinLoading } =
    useCoinPerStakedCoin(coinMode);
  const { data: apy, isLoading: apyLoading } = useApy();
  const { data: apr } = useApr();
  const grossApy = aprToGrossApy(apr);
  const apyList = useMemo(() => apyToApyList(apy), [apy]);

  return {
    loading: balanceLoading || coinPerStakedCoinLoading,
    balanceUsd,
    balanceLoading,
    balanceUsdLoading,
    croPerStcro: coinPerStakedCoin ?? undefined,
    isConnected,
    balance,
    account,
    apy,
    apyList,
    stakeFee,
    maxStakeAmount,
    apyLoading,
    grossApy,
  };
};

export default useStakeViewData;
